import React, { ChangeEvent, useState } from 'react'
import { Grid, Typography, InputAdornment } from '@mui/material'
import { useForm, UseFormReturn } from 'react-hook-form'
// TODO see if we can leverage a path alias for the base directory or root/src
import { CardIcon } from '../../../../assets/icons/card'
import { InfoIcon } from '../../../../assets/icons'
import { ItemObject } from '../../../../base/Dropdown/Dropdown'
import TextInput from '../../../../base/TextInput/TextInput'
import { inputProperties } from '../../../../types/inputProperties'
import { validators } from '../../../../utils/validators'
import Address from '../../address/Address'
import { COUNTRIES, CountryObject } from '../../../../utils/countries'

const defaultStyles = {
  instructionLabel: {
    width: '100%',
    marginBottom: '24px',
  },
}

/**
 * @interface CardPaymentMethodProps
 * @param {inputProperties} cardholderName (optional) - properties for field
 * @param {inputProperties} cardNumber (optional) - properties for field
 * @param {inputProperties} cvv (optional) - properties for field
 * @param {inputProperties} exp (optional) - properties for field
 * @param {inputProperties} props.line1 (optional) - properties for field
 * @param {inputProperties} props.line2 (optional) - properties for field
 * @param {inputProperties} props.city (optional) - properties for field
 * @param {inputProperties} props.state (optional) - properties for field
 * @param {inputProperties} props.postalCode (optional) - properties for field
 * @param {inputProperties} props.country (optional) - properties for field
 * @param {UseFormReturn} form (Optional) - hook for managing forms with ease
 * @param {string} instructionLabel (optional) - the label applied to the top of the form
 * @param {React.CSSProperties} instructionLabelStyles (optional) - styles applied to the intruction label
 */
export interface CardPaymentMethodProps {
  cardholderName?: inputProperties
  cardNumber?: inputProperties
  exp?: inputProperties
  line1?: inputProperties
  line2?: inputProperties
  city?: inputProperties
  state?: inputProperties
  postalCode?: inputProperties
  country?: inputProperties
  cvv?: inputProperties
  form?: UseFormReturn
  instructionLabel?: string
  instructionLabelStyles?: React.CSSProperties
}

/**
 * @param {CardPaymentMethodProps} props
 * @param {inputProperties} props.cardholderName (optional) - properties for field
 * @param {inputProperties} props.cardNumber (optional) - properties for field
 * @param {inputProperties} props.cvv (optional) - properties for field
 * @param {inputProperties} props.exp (optional) - properties for field
 * @param {inputProperties} props.line1 (optional) - properties for field
 * @param {inputProperties} props.line2 (optional) - properties for field
 * @param {inputProperties} props.city (optional) - properties for field
 * @param {inputProperties} props.state (optional) - properties for field
 * @param {inputProperties} props.postalCode (optional) - properties for field
 * @param {inputProperties} props.country (optional) - properties for field
 * @param {UseFormReturn} props.form (Optional) - hook for managing forms with ease
 * @param {string} props.instructionLabel (optional) - the label applied to the top of the form
 * @param {React.CSSProperties} props.instructionLabelStyles (optional) - styles applied to the intruction label
 */
const CardPaymentMethod = (props: CardPaymentMethodProps) => {
  const [cardValue, setCardValue] = useState('')
  const [expValue, setExpValue] = useState('')

  const cardholderNameProps = Object.assign(
    CardPaymentMethod.defaultProps.cardholderName,
    props.cardholderName,
  )
  const cardNumberProps = Object.assign(
    CardPaymentMethod.defaultProps.cardNumber,
    props.cardNumber,
  )
  const expProps = Object.assign(CardPaymentMethod.defaultProps.exp, props.exp)
  const cvvProps = Object.assign(CardPaymentMethod.defaultProps.cvv, props.cvv)
  Object.assign(CardPaymentMethod.defaultProps.address.country, props.country)
  Object.assign(CardPaymentMethod.defaultProps.address.line1, props.line1)
  Object.assign(CardPaymentMethod.defaultProps.address.line2, props.line2)
  Object.assign(CardPaymentMethod.defaultProps.address.city, props.city)
  Object.assign(CardPaymentMethod.defaultProps.address.state, props.state)
  Object.assign(
    CardPaymentMethod.defaultProps.address.postalCode,
    props.postalCode,
  )

  const handleCardChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCardValue(event.target.value)
  }

  function cardMask(event: React.ChangeEvent<HTMLInputElement>) {
    let value = event.target.value
    const nonDigit = /\D/g
    const thousandSeparator = /(\d{4})\B/g
    value = value.replace(nonDigit, '')
    value = value.replace(thousandSeparator, '$1 ')
    event.target.value = value
    return event
  }

  const handleExpChange = (event: ChangeEvent<HTMLInputElement>) => {
    setExpValue(event.target.value)
  }
  function expMask(event: React.ChangeEvent<HTMLInputElement>) {
    let value = event.target.value
    const nonDigit = /\D/g
    const thousandSeparator = /(\d{2})\B/g
    value = value.replace(nonDigit, '')
    value = value.replace(thousandSeparator, '$1/')
    event.target.value = value
    return event
  }

  //Countries in format ItemObject
  const menuItems: Array<ItemObject> = COUNTRIES?.map(
    (item: CountryObject) => ({
      value: item.abbreviation,
      label: item.name,
      className: `flag flagSpacing ${item.abbreviation}`,
    }),
  )

  const useFormDefault = props.form || useForm()
  return (
    <>
      <Typography style={defaultStyles.instructionLabel}>
        {props.instructionLabel}
      </Typography>
      <TextInput
        form={useFormDefault}
        disabled={cardholderNameProps?.disabled}
        id={cardholderNameProps?.id}
        label={cardholderNameProps?.label}
        name={cardholderNameProps?.name}
        rules={{ validate: validators.name('Cardholder Name') }}
        required={cardholderNameProps?.required}
        value={cardholderNameProps?.value}
      />

      <TextInput
        form={useFormDefault}
        disabled={cardNumberProps?.disabled}
        id={cardNumberProps?.id}
        inputProps={{
          value: cardValue,
          maxLength: 19,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <CardIcon />
            </InputAdornment>
          ),
        }}
        label={cardNumberProps?.label}
        name={cardNumberProps?.name}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleCardChange(cardMask(event))
        }
        required={cardNumberProps?.required}
        rules={{ validate: validators.validateCard }}
        value={cardNumberProps?.value}
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextInput
            form={useFormDefault}
            disabled={expProps?.disabled}
            id={expProps?.id}
            inputProps={{
              value: expValue,
              maxLength: 5,
            }}
            label={expProps?.label}
            name={expProps?.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleExpChange(expMask(event))
            }
            required={expProps?.required}
            rules={{ validate: validators.validateExp }}
            value={expProps?.value}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            form={useFormDefault}
            disabled={cvvProps?.disabled}
            id={cvvProps?.id}
            inputProps={{ maxLength: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <InfoIcon />
                </InputAdornment>
              ),
            }}
            label={cvvProps?.label}
            name={cvvProps?.name}
            required={cvvProps?.required}
            rules={{ validate: validators.validateCVV }}
            value={cvvProps?.value}
          />
        </Grid>
      </Grid>
      <Address
        form={useFormDefault}
        {...CardPaymentMethod.defaultProps.address}
      />
    </>
  )
}

CardPaymentMethod.defaultProps = {
  cardholderName: {
    label: 'Cardholder Name',
    name: 'cardholderName',
  },
  cardNumber: {
    label: 'Card Number',
    name: 'cardNumber',
  },
  cvv: {
    label: 'CVV',
    name: 'cvv',
  },
  exp: {
    label: 'EXP',
    name: 'exp',
  },
  address: {
    country: {
      name: 'country',
      required: true,
    },
    line1: {
      name: 'line1',
      required: true,
    },
    line2: {
      name: 'line1',
    },
    city: {
      name: 'city',
      required: true,
    },
    state: {
      name: 'state',
      required: true,
    },
    postalCode: {
      name: 'postalCode',
      required: true,
    },
  },
  instructionLabel:
    'Please note a fee may apply to card payments',
}

export default CardPaymentMethod
